import { Actions_, Builder_, Dropdown_, FormBox, FormBoxSelection, Grey_, IconSvg, Input_, Main_, Main_Plain, Toggle_Stat } from 'monica-alexandria';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Location_Input } from '../../../../components/location/Location_Input';
import { get_business_categories } from '../../newLead/utils/business_categories';
import { Quest_Provider_Counts } from '../../../../components/quest/Quest_Provider_Counts';
import { read_seller_identity_keys } from '../../../../utils/hr/seller_identity';

const LeadsBrand = styled.div`
    display: grid;
    gap: 2rem;

    .Builder{
        grid-template-columns: 1fr 1fr;
    }
`;

const LeadsBrandSelection = styled(FormBoxSelection)`
    display: grid;
    gap: 0.4rem;

    p {
        word-break: break-word;
        white-space: pre-wrap;
    }
`;

const LeadsBrandImported = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: ${p => p.theme.mid};

    svg {
        width: 1.4rem;
        height: 1.4rem;
    }
`;

const LeadsBrandActions = styled.div`
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
`;

const LeadsBrandAssignment = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-bottom: 1rem;
`;

const LeadsBrandQuestCounts = styled(FormBoxSelection)`
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.8rem;
    flex-wrap: wrap;
    padding: var(--smallPads);
    border: 0.1rem solid ${p => p.theme.low};
    border-radius: var(--smallRadius);
    background: ${p => p.theme.low};

    > p {
        color: ${p => p.theme.color};
        font-weight: 700;
        white-space: nowrap;
    }
`;

const LeadsBrandNames = styled.div`
    display: grid;
    gap: 0.8rem;
`;

/**
 * Reads the display text for a locked FormBox field.
 */
const read_field_text = (value, fallback) => (Array.isArray(value) ? value : [value]).map((item) => `${item || ''}`.trim()).filter(Boolean).join(', ') || fallback;

/**
 * Reads the name assigned internally by an admin.
 */
const read_brand_internal_name = (brand) => `${brand?.internalName || brand?.name || ''}`.trim();

/**
 * Reads the canonical DIF code while supporting older compatibility fields.
 */
const read_brand_dif_code = (brand) => {
    const value = `${brand?.difCode || brand?.clientCode || brand?.code || ''}`.trim();

    if (!value) {
        return '';
    }

    return /^DIF-/i.test(value) ? value.replace(/^DIF-/i, 'DIF-') : `DIF-${value}`;
};

/**
 * Renders the imported marker used by migrated Brand fields.
 */
const Imported_Field = ({ children, imported, t }) => (
    <LeadsBrandSelection>
        {imported ? (
            <LeadsBrandImported title={t('Imported')}>
                <IconSvg Icon="Download" />
                <h6>{t('Imported')}</h6>
            </LeadsBrandImported>
        ) : null}
        {children}
    </LeadsBrandSelection>
);

/**
 * Adds lead source metadata to a brand row before import-lock checks.
 */
const read_brand_with_lead_source = (brand, lead) => ({
    ...brand,
    source: brand?.source || lead?.source,
    sourceApp: brand?.sourceApp || lead?.sourceApp,
    sourceSystem: brand?.sourceSystem || lead?.sourceSystem,
    platformSource: brand?.platformSource || lead?.platformSource,
    submittedBy: brand?.submittedBy || lead?.submittedBy,
    submittedByName: brand?.submittedByName || lead?.submittedByName,
    sourceUrl: brand?.sourceUrl || lead?.sourceUrl,
    sourceURL: brand?.sourceURL || lead?.sourceURL,
    legacyLeadId: brand?.legacyLeadId || lead?.legacyLeadId,
    legacyProspectId: brand?.legacyProspectId || lead?.legacyProspectId
});

/**
 * Reads explicit migration metadata without exposing a second Client entity.
 */
const read_brand_imported_fields = (brand) => {
    return brand?.importedFields || {};
};

/**
 * Reads whether one displayed brand field is locked because it was imported.
 */
const read_brand_field_imported = (brand, field) => Boolean(read_brand_imported_fields(brand)?.[field]);

/**
 * Keeps imported metadata visible without blocking user edits.
 */
const read_brand_field_locked = () => false;

/**
 * Reads whether one brand row is the currently open add-brand editor.
 */
const read_brand_is_open_draft = (brand) => Boolean(brand?.draftBrand);

/**
 * Reads whether one brand field has a non-empty value in text or list form.
 */
const read_brand_field_has_value = (...values) => Boolean([].concat(...values).map((value) => `${value || ''}`.trim()).filter(Boolean).length);

/**
 * Reads whether one brand has every field required before adding the next brand.
 */
const read_brand_has_required_fields = (brand) => {
    const requiredFieldsFilled = [
        read_brand_field_has_value(brand?.internalName, brand?.name, brand?.brand),
        read_brand_field_has_value(brand?.category),
        read_brand_field_has_value(brand?.subcategory),
        read_brand_field_has_value(brand?.phones, brand?.phone, brand?.phoneList),
        read_brand_field_has_value(brand?.emails, brand?.email, brand?.emailList)
    ];

    return requiredFieldsFilled.every(Boolean);
};

/**
 * Reads whether the open draft brand is complete before another draft can be added.
 */
const read_previous_brand_is_complete = (selectedLead) => {
    const draftBrand = (selectedLead?.brands || []).find((brand) => read_brand_is_open_draft(brand));

    if (!draftBrand) {
        return true;
    }

    return read_brand_has_required_fields(draftBrand);
};

/**
 * Updates one simple field inside a nested brand draft.
 */
const update_brand_field = (selectedLead, setSelectedLead, brandIndex, field, value) => {
    setSelectedLead((previousLead) => {
        const currentLead = previousLead || selectedLead || {};

        return {
            ...currentLead,
            edit: true,
            brands: (currentLead?.brands || []).map((brand, currentBrandIndex) => {
                if (currentBrandIndex !== brandIndex) {
                    return brand;
                }

                if (field === 'category') {
                    return {
                        ...brand,
                        category: value,
                        subcategory: ''
                    };
                }

                return {
                    ...brand,
                    [field]: value
                };
            })
        };
    });
};

/**
 * Keeps the canonical name alias aligned with the admin-owned internal name.
 */
const update_brand_internal_name = (selectedLead, setSelectedLead, brandIndex, value) => {
    setSelectedLead((previousLead) => {
        const currentLead = previousLead || selectedLead || {};

        return {
            ...currentLead,
            edit: true,
            brands: (currentLead?.brands || []).map((brand, currentBrandIndex) => currentBrandIndex === brandIndex ? {
                ...brand,
                name: value,
                internalName: value
            } : brand)
        };
    });
};

/**
 * Adds one value into a nested brand list field.
 */
const add_brand_list_value = (selectedLead, setSelectedLead, brandIndex, field, draftField) => {
    const nextValue = `${selectedLead?.brands?.[brandIndex]?.[draftField] || ''}`.trim();

    if (!nextValue) {
        return;
    }

    setSelectedLead((previousLead) => {
        const currentLead = previousLead || selectedLead || {};
        const currentValue = `${currentLead?.brands?.[brandIndex]?.[draftField] || nextValue}`.trim();

        return {
            ...currentLead,
            edit: true,
            brands: (currentLead?.brands || []).map((brand, currentBrandIndex) => currentBrandIndex === brandIndex ? {
                ...brand,
                [field]: [...(brand?.[field] || []), currentValue],
                [draftField]: ''
            } : brand)
        };
    });
};

/**
 * Deletes one value from a nested brand list field.
 */
const delete_brand_list_value = (selectedLead, setSelectedLead, brandIndex, field, valueIndex) => {
    const brand = read_brand_with_lead_source(selectedLead?.brands?.[brandIndex] || {}, selectedLead);

    if (read_brand_field_locked(brand, field)) {
        return;
    }

    setSelectedLead((previousLead) => {
        const currentLead = previousLead || selectedLead || {};

        return {
            ...currentLead,
            edit: true,
            brands: (currentLead?.brands || []).map((brand, currentBrandIndex) => currentBrandIndex === brandIndex ? {
                ...brand,
                [field]: (brand?.[field] || []).filter((value, currentValueIndex) => currentValueIndex !== valueIndex)
            } : brand)
        };
    });
};

/**
 * Updates the temporary phone input for one nested brand.
 */
const update_brand_phone_draft = (selectedLead, setSelectedLead, brandIndex, value) => {
    update_brand_field(selectedLead, setSelectedLead, brandIndex, 'phoneDraft', value);
};

/**
 * Adds one phone value into the nested brand phones array.
 */
const add_brand_phone = (selectedLead, setSelectedLead, brandIndex) => {
    add_brand_list_value(selectedLead, setSelectedLead, brandIndex, 'phones', 'phoneDraft');
};

/**
 * Deletes one phone value from the nested brand phones array.
 */
const delete_brand_phone = (selectedLead, setSelectedLead, brandIndex, phoneIndex) => {
    delete_brand_list_value(selectedLead, setSelectedLead, brandIndex, 'phones', phoneIndex);
};

/**
 * Saves the currently selected Leaflet location into the nested brand.
 */
const save_brand_location = (selectedLead, setSelectedLead, brandIndex) => {
    const nextLocation = `${selectedLead?.brands?.[brandIndex]?.locationDraft || ''}`.trim();

    if (!nextLocation) {
        return;
    }

    setSelectedLead((previousLead) => {
        const currentLead = previousLead || selectedLead || {};
        const currentLocation = `${currentLead?.brands?.[brandIndex]?.locationDraft || nextLocation}`.trim();

        return {
            ...currentLead,
            edit: true,
            brands: (currentLead?.brands || []).map((brand, currentBrandIndex) => currentBrandIndex === brandIndex ? {
                ...brand,
                location: currentLocation,
                locationDraft: ''
            } : brand)
        };
    });
};

/**
 * Clears the saved location from a nested brand.
 */
const delete_brand_location = (selectedLead, setSelectedLead, brandIndex) => {
    setSelectedLead((previousLead) => {
        const currentLead = previousLead || selectedLead || {};

        return {
            ...currentLead,
            edit: true,
            brands: (currentLead?.brands || []).map((brand, currentBrandIndex) => currentBrandIndex === brandIndex ? {
                ...brand,
                location: ''
            } : brand)
        };
    });
};

/**
 * Adds one empty nested brand into the current lead draft.
 */
const add_brand = (selectedLead, setSelectedLead, setBrandError) => {
    if (!read_previous_brand_is_complete(selectedLead)) {
        return;
    }

    setBrandError('');

    setSelectedLead((previousLead) => {
        const currentLead = previousLead || selectedLead || {};

        return {
            ...currentLead,
            edit: true,
            brands: [
                ...(currentLead?.brands || []).map((brand) => read_brand_is_open_draft(brand) ? {
                    ...brand,
                    draftBrand: false
                } : brand),
                {
                    ownerFirstName: currentLead?.firstName || '',
                    ownerLastName: currentLead?.lastName || '',
                    name: '',
                    internalName: '',
                    actualBusinessName: '',
                    category: '',
                    subcategory: '',
                    location: '',
                    seller: null,
                    phones: [],
                    emails: [],
                    paidStatus: 'none',
                    status: 'Backlog',
                    comments: [],
                    history: [],
                    draftBrand: true,
                    emailDraft: '',
                    locationDraft: '',
                    phoneDraft: ''
                }
            ]
        };
    });
};

/**
 * Removes one open draft brand from the current lead draft.
 */
const cancel_brand = (selectedLead, setSelectedLead, brandIndex) => {
    setSelectedLead((previousLead) => {
        const currentLead = previousLead || selectedLead || {};

        return {
            ...currentLead,
            edit: true,
            brands: (currentLead?.brands || []).filter((brand, currentBrandIndex) => currentBrandIndex !== brandIndex)
        };
    });
};

/**
 * Closes the add-brand editor and keeps the new brand in the current slider draft.
 */
const save_brand = (selectedLead, setSelectedLead, brandIndex, setBrandError) => {
    const brand = selectedLead?.brands?.[brandIndex] || {};

    if (!read_brand_has_required_fields(brand)) {
        return;
    }

    setBrandError('');

    setSelectedLead((previousLead) => {
        const currentLead = previousLead || selectedLead || {};

        return {
            ...currentLead,
            edit: true,
            brands: (currentLead?.brands || []).map((brand, currentBrandIndex) => currentBrandIndex === brandIndex ? {
                ...brand,
                draftBrand: false
            } : brand)
        };
    });
};

/**
 * Renders the prospect and nested brand data as read-only FormBoxes.
 */
export default function Leads_Brand(props) {
    const { t } = useTranslation();
    const businessCategories = get_business_categories(t);
    const [brandError, setBrandError] = useState('');
    const previousBrandIsComplete = read_previous_brand_is_complete(props?.selectedLead);

    return (
        <LeadsBrand>
            {(props?.selectedLead?.brands || []).map((brandItem, brandIndex) => {
                const brand = read_brand_with_lead_source(brandItem, props?.selectedLead);
                const difCode = read_brand_dif_code(brand);
                const assigneeKeys = read_seller_identity_keys(brand?.seller || brand?.assignee || brand?.sellerId);
                const currentSellerKeys = read_seller_identity_keys(props?.currentSeller);
                const assigned = assigneeKeys.length > 0;
                const assignedToMe = assigned && assigneeKeys.some((assigneeKey) => currentSellerKeys.includes(assigneeKey));
                const assigning = `${props?.assigningBrandId || ''}` === `${brand?._id || ''}`;
                const assignmentLocked = !brand?._id || assigned || assigning || !props?.canAssignToMe;

                return (
                    <FormBox key={brand?._id || brandIndex}>
                        {!read_brand_is_open_draft(brand) && brand?._id ? (
                            <LeadsBrandAssignment>
                                <Toggle_Stat
                                    active={assignedToMe || assigning}
                                    text={t('Assign to me')}
                                    onClick={() => assignmentLocked ? false : props?.onAssignToMe(brand)}
                                />
                            </LeadsBrandAssignment>
                        ) : null}
                        <Builder_>
                            <LeadsBrandQuestCounts>
                                <Quest_Provider_Counts brand={brand} t={t} />
                                {difCode ? <p>{difCode}</p> : null}
                            </LeadsBrandQuestCounts>
                        </Builder_>
                        <Builder_ split>
                            <LeadsBrandNames>
                            <Imported_Field imported={read_brand_field_imported(brand, 'name')} t={t}>
                                <h6>{t('Internal name')}</h6>
                                {read_brand_is_open_draft(brand) && !read_brand_field_locked(brand, 'name') ? (
                                    <Input_
                                        placeholder={t('Internal name')}
                                        value={read_brand_internal_name(brand)}
                                        onChange={(event) => {
                                            setBrandError('');
                                            update_brand_internal_name(props?.selectedLead, props?.setSelectedLead, brandIndex, event.target.value);
                                        }}
                                    />
                                ) : (
                                    <p>{read_brand_internal_name(brand) || t('No internal name')}</p>
                                )}
                            </Imported_Field>
                            <LeadsBrandSelection>
                                <h6>{t('Business name')}</h6>
                                <p>{brand?.actualBusinessName || t('No business name')}</p>
                            </LeadsBrandSelection>
                        </LeadsBrandNames>
                        {read_brand_is_open_draft(brand) && !read_brand_field_locked(brand, 'location') ? (
                            <Location_Input
                                placeholder={t('Location')}
                                value={brand?.locationDraft || ''}
                                list={brand?.location ? [brand.location] : []}
                                icon_ListItem="Bullet"
                                add={t('Add')}
                                onChange={(event) => update_brand_field(props?.selectedLead, props?.setSelectedLead, brandIndex, 'locationDraft', event.target.value)}
                                onAdd={() => save_brand_location(props?.selectedLead, props?.setSelectedLead, brandIndex)}
                                onDelete={() => delete_brand_location(props?.selectedLead, props?.setSelectedLead, brandIndex)}
                            />
                        ) : (
                            <Imported_Field imported={read_brand_field_imported(brand, 'location')} t={t}>
                                <p>{brand?.location || ''}</p>
                            </Imported_Field>
                        )}
                    </Builder_>
                    <Builder_ split>
                        {read_brand_is_open_draft(brand) && !read_brand_field_locked(brand, 'category') ? (
                            <Dropdown_ label={t('Category')}>
                                <select
                                    value={brand?.category || ''}
                                    onChange={(event) => update_brand_field(props?.selectedLead, props?.setSelectedLead, brandIndex, 'category', event.target.value)}
                                >
                                    <option value="">{t('Category')}</option>
                                    {Object.keys(businessCategories).map((category) => (
                                        <option key={category} value={category}>
                                            {category}
                                        </option>
                                    ))}
                                </select>
                            </Dropdown_>
                        ) : (
                            <Imported_Field imported={read_brand_field_imported(brand, 'category')} t={t}>
                                <p>{brand?.category || t('No Category')}</p>
                            </Imported_Field>
                        )}
                        {read_brand_is_open_draft(brand) && !read_brand_field_locked(brand, 'subcategory') ? (
                            <Dropdown_ label={t('Subcategory')}>
                                <select
                                    value={brand?.subcategory || ''}
                                    onChange={(event) => update_brand_field(props?.selectedLead, props?.setSelectedLead, brandIndex, 'subcategory', event.target.value)}
                                >
                                    <option value="">{t('Subcategory')}</option>
                                    {(businessCategories[brand?.category] || []).map((subcategory) => (
                                        <option key={subcategory} value={subcategory}>
                                            {subcategory}
                                        </option>
                                    ))}
                                </select>
                            </Dropdown_>
                        ) : (
                            <Imported_Field imported={read_brand_field_imported(brand, 'subcategory')} t={t}>
                                <p>{brand?.subcategory || t('No Subcategory')}</p>
                            </Imported_Field>
                        )}
                    </Builder_>
                    <Builder_ last split>
                        {read_brand_is_open_draft(brand) && !read_brand_field_locked(brand, 'emails') ? (
                            <Input_
                                placeholder={t('Email')}
                                value={brand?.emailDraft || ''}
                                list={brand?.emails || []}
                                icon_ListItem="Email"
                                add={t('Add')}
                                onChange={(event) => update_brand_field(props?.selectedLead, props?.setSelectedLead, brandIndex, 'emailDraft', event.target.value)}
                                onAdd={() => add_brand_list_value(props?.selectedLead, props?.setSelectedLead, brandIndex, 'emails', 'emailDraft')}
                                onDelete={(emailIndex) => delete_brand_list_value(props?.selectedLead, props?.setSelectedLead, brandIndex, 'emails', emailIndex)}
                            />
                        ) : (
                            <Imported_Field imported={read_brand_field_imported(brand, 'emails')} t={t}>
                                <p>{read_field_text(brand?.emails, '')}</p>
                            </Imported_Field>
                        )}
                        {read_brand_is_open_draft(brand) && !read_brand_field_locked(brand, 'phones') ? (
                            <Input_
                                placeholder={t('Phone')}
                                value={brand?.phoneDraft || ''}
                                list={brand?.phones || []}
                                icon_ListItem="Phone"
                                add={t('Add')}
                                onChange={(event) => update_brand_phone_draft(props?.selectedLead, props?.setSelectedLead, brandIndex, event.target.value)}
                                onAdd={() => add_brand_phone(props?.selectedLead, props?.setSelectedLead, brandIndex)}
                                onDelete={(phoneIndex) => delete_brand_phone(props?.selectedLead, props?.setSelectedLead, brandIndex, phoneIndex)}
                            />
                        ) : (
                            <Imported_Field imported={read_brand_field_imported(brand, 'phones')} t={t}>
                                <p>{read_field_text(brand?.phones, '')}</p>
                            </Imported_Field>
                        )}
                    </Builder_>
                    {read_brand_is_open_draft(brand) ? (
                        <Actions_>
                            <Grey_
                                cancel
                                text={t('Cancel')}
                                iconLeft="Xicon"
                                onClick={() => cancel_brand(props?.selectedLead, props?.setSelectedLead, brandIndex)}
                            />
                            <Main_
                                save
                                text={t('Save')}
                                iconLeft="Save"
                                onClick={() => save_brand(props?.selectedLead, props?.setSelectedLead, brandIndex, setBrandError)}
                            />
                        </Actions_>
                    ) : null}
                </FormBox>
                );
            })}
            {brandError ? (
                <LeadsBrandSelection>
                    <p>{brandError}</p>
                </LeadsBrandSelection>
            ) : null}
            <LeadsBrandActions>
                <Main_Plain
                    text={t('Add brand')}
                    iconLeft="Add"
                    disabled={!previousBrandIsComplete}
                    onClick={() => add_brand(props?.selectedLead, props?.setSelectedLead, setBrandError)}
                />
            </LeadsBrandActions>
        </LeadsBrand>
    );
}

export { Leads_Brand };
